import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import { endpoints, SSO_PROVIDERS, type SsoProvider } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ErrorBox, errorMessage, Notice } from '../components/common';

const SSO_NAMES: Record<SsoProvider, string> = { google: 'Google', microsoft: 'Microsoft', okta: 'Okta' };

function AuthFrame({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <main id="main" className="auth-page">
      <div className="card auth-card">
        <div className="auth-header">
          <h1>{t('app.name')}</h1>
          <LanguageSwitcher />
        </div>
        <p className="muted">{t('app.tagline')}</p>
        <h2>{title}</h2>
        {children}
      </div>
    </main>
  );
}

// Пока настройки не получены (или недоступны), регистрация считается открытой: запрет проверяет сервер.
function useRegistrationEnabled() {
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    endpoints.auth
      .settings()
      .then((s) => setEnabled(s.registration_enabled !== false))
      .catch(() => undefined);
  }, []);
  return enabled;
}

function SsoButtons() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<SsoProvider[]>(SSO_PROVIDERS);
  useEffect(() => {
    endpoints.auth
      .ssoProviders()
      .then((list) => setEnabled(SSO_PROVIDERS.filter((p) => list.includes(p))))
      .catch(() => undefined);
  }, []);
  return (
    <div className="sso" role="group" aria-label={t('auth.or')}>
      {enabled.map((p) => (
        <a key={p} className="button secondary" href={endpoints.auth.ssoLoginUrl(p)}>
          {t('auth.sso', { provider: SSO_NAMES[p] })}
        </a>
      ))}
      <a className="button secondary" href={endpoints.auth.samlLoginUrl()}>
        {t('auth.saml')}
      </a>
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const registered = (location.state as { registered?: boolean } | null)?.registered;
  const registrationEnabled = useRegistrationEnabled();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/login' ? from : '/', { replace: true });
    } catch (err) {
      setError(t('auth.loginFailed', { message: errorMessage(err) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={t('auth.login')}>
      {registered && <Notice>{t('auth.registered')}</Notice>}
      <form onSubmit={submit} className="form">
        <label>
          {t('auth.email')}
          <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          {t('auth.password')}
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error && (
          <p role="alert" className="alert alert-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {busy ? t('auth.signingIn') : t('auth.login')}
        </button>
      </form>
      <p className="divider">{t('auth.or')}</p>
      <SsoButtons />
      {registrationEnabled && (
        <p>
          {t('auth.noAccount')} <Link to="/register">{t('auth.register')}</Link>
        </p>
      )}
      <p>
        <Link to="/developers">{t('nav.apiDocs')}</Link>
      </p>
    </AuthFrame>
  );
}

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { acceptTokens } = useAuth();
  const registrationEnabled = useRegistrationEnabled();
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await endpoints.auth.register(form);
      if (result?.tokens) {
        await acceptTokens(result);
        navigate('/', { replace: true });
      } else {
        navigate('/login', { replace: true, state: { registered: true } });
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!registrationEnabled) {
    return (
      <AuthFrame title={t('auth.register')}>
        <Notice>{t('auth.registrationClosed')}</Notice>
        <p>
          {t('auth.haveAccount')} <Link to="/login">{t('auth.login')}</Link>
        </p>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={t('auth.register')}>
      <form onSubmit={submit} className="form">
        <label>
          {t('auth.fullName')}
          <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </label>
        <label>
          {t('auth.email')}
          <input
            type="email"
            autoComplete="username"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label>
          {t('auth.password')}
          <input
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </label>
        <ErrorBox error={error} />
        <button type="submit" disabled={busy}>
          {t('auth.register')}
        </button>
      </form>
      <p>
        {t('auth.haveAccount')} <Link to="/login">{t('auth.login')}</Link>
      </p>
    </AuthFrame>
  );
}

export function safeNext(value: string | null | undefined): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

export function parseSsoResult(
  hash: string,
  search: string,
): { tokens?: { access_token: string; refresh_token: string }; code?: string; error?: string; next: string } {
  const h = new URLSearchParams(hash.replace(/^#/, ''));
  const q = new URLSearchParams(search);
  const access = h.get('access_token') ?? q.get('access_token');
  const refresh = h.get('refresh_token') ?? q.get('refresh_token');
  const next = safeNext(h.get('next') ?? q.get('next'));
  if (access && refresh) return { tokens: { access_token: access, refresh_token: refresh }, next };
  const code = h.get('code') ?? q.get('code');
  if (code) return { code, next };
  return { error: h.get('error') ?? q.get('error') ?? 'missing_token', next };
}

export function SsoCallbackPage() {
  const { t } = useTranslation();
  const { acceptTokens } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const result = parseSsoResult(window.location.hash, window.location.search);
    window.history.replaceState(null, '', window.location.pathname);
    (async () => {
      try {
        if (result.tokens) await acceptTokens(result.tokens);
        else if (result.code) await acceptTokens(await endpoints.auth.exchangeCode(result.code));
        else throw new ApiError(0, result.error ?? 'missing_token', result.error ?? '');
        navigate(result.next, { replace: true });
      } catch (err) {
        setError(`${t('auth.ssoFailed')}: ${errorMessage(err)}`);
      }
    })();
  }, [acceptTokens, navigate, t]);

  return (
    <AuthFrame title={t('auth.signingIn')}>
      {error ? (
        <p role="alert" className="alert alert-error">
          {error} <Link to="/login">{t('auth.login')}</Link>
        </p>
      ) : (
        <p role="status">{t('auth.signingIn')}</p>
      )}
    </AuthFrame>
  );
}
